import { useMemo } from 'react';

import type { KidMarker } from '../../../../../../../api/kids/types';
import type { SetTableTag } from '../../../types/KidPdfGeneratorV2.types';
import type {
  PresetKidMarkersContext,
  PresetMarkerSource,
} from '../../../hooks/usePresetKidMarkers';
import { usePresetKidMarkers } from '../../../hooks/usePresetKidMarkers';
import {
  buildCompositionOfCostsTableFromMarkers,
  buildCostsOverTimeTableFromMarkers,
  buildPerformanceScenariosTableFromMarkers,
} from './buildTablesFromMarkers';
import { usePresetTableContent } from './usePresetTableContent';

interface SetTableConfig {
  label: string;
  source: PresetMarkerSource;
  build: (markers: KidMarker[]) => string[][];
}

/** Per-tag config for KID set-table blocks: display label, marker source, and content builder. */
export const SET_TABLE_CONFIG: Record<SetTableTag, SetTableConfig> = {
  costs_over_time_table: {
    label: 'Costs Over Time',
    source: 'figure',
    build: buildCostsOverTimeTableFromMarkers,
  },
  composition_of_costs_table: {
    label: 'Composition of Costs',
    source: 'cost',
    build: buildCompositionOfCostsTableFromMarkers,
  },
  performance_scenarios_table: {
    label: 'Performance Scenarios',
    source: 'figure',
    build: buildPerformanceScenariosTableFromMarkers,
  },
};

/** Props for the presentational set-table components rendered by KidSetTableInput. */
export interface SetTableViewProps {
  content: string[][];
  updateCell: (rowIndex: number, colIndex: number, value: string) => void;
  isInjectedCell: (rowIndex: number, colIndex: number) => boolean;
}

/**
 * Seed hook for KID set-table blocks: fetches the COST/FIG markers for the tag,
 * builds the table content, and syncs it onto the form element.
 */
export function useSeededKidSetTable(
  index: number,
  tag: SetTableTag,
  markerContext?: PresetKidMarkersContext,
) {
  const { label, source, build } = SET_TABLE_CONFIG[tag];

  const { markers, isLoading } = usePresetKidMarkers(
    markerContext ?? {},
    source,
  );

  const markerBuiltContent = useMemo(() => build(markers), [build, markers]);

  const table = usePresetTableContent(index, tag, markers, markerBuiltContent);

  return {
    ...table,
    label,
    source,
    isLoading,
    hasMarkers: markers.length > 0,
  };
}
